import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plane, Clock, CheckCircle, Users, Ticket, DollarSign, ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasajerosPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { status } = useParams(); // 'pendientes' or 'completados'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGestors, setExpandedGestors] = useState({});

  const isPending = status === 'pendientes';
  const pageTitle = isPending ? 'Pasajeros Pendientes' : 'Pasajeros Completados';
  const pageIcon = isPending ? Clock : CheckCircle;
  const PageIcon = pageIcon;

  useEffect(() => {
    fetchData();
  }, [status]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/ventas/grouped-by-status`, {
        headers: getAuthHeaders()
      });
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleGestor = (gestorId) => {
    setExpandedGestors(prev => ({
      ...prev,
      [gestorId]: !prev[gestorId]
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const gestorList = data ? (isPending ? data.pending : data.completed) : [];
  const totalCount = data ? (isPending ? data.total_pending : data.total_completed) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid={`pasajeros-${status}-page`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <PageIcon className={`w-8 h-8 ${isPending ? 'text-yellow-500' : 'text-green-500'}`} />
              {pageTitle}
            </h1>
            <p className="text-slate-500 mt-1">
              {totalCount} pasajeros {isPending ? 'por volar' : 'que ya volaron'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={isPending ? 'default' : 'outline'}
            onClick={() => navigate('/pasajeros/pendientes')}
            className={isPending ? 'bg-yellow-500 hover:bg-yellow-600 text-slate-900' : ''}
          >
            <Clock className="w-4 h-4 mr-2" />
            Pendientes ({data?.total_pending || 0})
          </Button>
          <Button
            variant={!isPending ? 'default' : 'outline'}
            onClick={() => navigate('/pasajeros/completados')}
            className={!isPending ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Completados ({data?.total_completed || 0})
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={`shadow-sm border-l-4 ${isPending ? 'border-l-yellow-500' : 'border-l-green-500'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`p-3 rounded-lg ${isPending ? 'bg-yellow-100' : 'bg-green-100'}`}>
              <Users className={`w-6 h-6 ${isPending ? 'text-yellow-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Gestores con Pasajeros</p>
              <p className="text-2xl font-bold text-slate-900">{gestorList.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Ticket className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Pasajeros</p>
              <p className="text-2xl font-bold text-slate-900">{totalCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-l-4 border-l-purple-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total en Ventas</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(gestorList.reduce((sum, g) => sum + g.total_amount, 0))}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gestors List with Expandable Passengers */}
      {gestorList.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-8 text-center text-slate-500">
            <PageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No hay pasajeros {isPending ? 'pendientes' : 'completados'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {gestorList.map((gestor, index) => (
            <Card key={gestor.gestor_id} className="shadow-sm overflow-hidden">
              <Collapsible open={expandedGestors[gestor.gestor_id]} onOpenChange={() => toggleGestor(gestor.gestor_id)}>
                <CollapsibleTrigger asChild>
                  <div className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${expandedGestors[gestor.gestor_id] ? 'bg-slate-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                          index === 1 ? 'bg-slate-200 text-slate-600' : 
                          index === 2 ? 'bg-amber-100 text-amber-700' : 
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {index < 3 ? `#${index + 1}` : gestor.gestor_name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 text-lg">{gestor.gestor_name}</h3>
                          <p className="text-sm text-slate-500">
                            {gestor.count} pasajero{gestor.count > 1 ? 's' : ''} • {formatCurrency(gestor.total_amount)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={isPending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                          {gestor.count} {isPending ? 'pendiente' : 'completado'}{gestor.count > 1 ? 's' : ''}
                        </Badge>
                        {expandedGestors[gestor.gestor_id] ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Pasajero</TableHead>
                          <TableHead>Ruta</TableHead>
                          <TableHead>Fecha Salida</TableHead>
                          <TableHead>Fecha Retorno</TableHead>
                          <TableHead className="text-right">Pasajes</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gestor.ventas.map((venta) => (
                          <TableRow key={venta.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{venta.passenger_name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <span>{venta.route}</span>
                              </div>
                            </TableCell>
                            <TableCell>{venta.flight_date_departure}</TableCell>
                            <TableCell>{venta.flight_date_return || '-'}</TableCell>
                            <TableCell className="text-right">{venta.tickets_count}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(venta.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasajerosPage;
